import { Phone, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { GsShield } from "./GsShield";
import { useLeadFunnel } from "@/contexts/LeadFunnelContext";
import { lovable } from "@/integrations/lovable";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.74-6-6.1s2.7-6.1 6-6.1c1.9 0 3.16.8 3.88 1.5l2.65-2.55C16.84 3.4 14.66 2.4 12 2.4 6.84 2.4 2.7 6.54 2.7 11.7s4.14 9.3 9.3 9.3c5.37 0 8.93-3.77 8.93-9.07 0-.61-.07-1.07-.16-1.53H12z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" fill="currentColor">
    <path d="M16.365 1.43c0 1.14-.45 2.23-1.18 3.04-.78.86-2.05 1.52-3.1 1.44-.13-1.12.42-2.27 1.13-3.02.79-.84 2.13-1.46 3.15-1.46zm3.4 17.49c-.62 1.36-.92 1.97-1.72 3.18-1.11 1.69-2.68 3.79-4.62 3.81-1.72.02-2.16-1.12-4.49-1.11-2.33.02-2.82 1.13-4.54 1.11-1.94-.02-3.42-1.92-4.53-3.6-3.1-4.71-3.43-10.24-1.51-13.18 1.36-2.08 3.51-3.3 5.53-3.3 2.06 0 3.36 1.13 5.06 1.13 1.65 0 2.66-1.13 5.04-1.13 1.8 0 3.71.98 5.07 2.68-4.46 2.44-3.74 8.81 1.71 10.41z"/>
  </svg>
);

const navLinks = [
  { href: "#top", label: "Home" },
  { href: "#services", label: "Services" },
  { href: "#projects", label: "Work" },
  { href: "#about", label: "About" },
  { href: "#reviews", label: "Reviews" },
];

export const Header = () => {
  const { open } = useLeadFunnel();
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({ data }) => setUser(data.session?.user ?? null));
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleGoogle = async () => {
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      toast.error("Sign-in failed. Please try again.");
      return;
    }
  };

  const handleApple = async () => {
    const result = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      toast.error("Sign-in failed. Please try again.");
      return;
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success("Signed out");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-warm-white/10 bg-charcoal/90 backdrop-blur-md">
      <div className="container-tight flex h-20 items-center justify-between gap-6">
        <a href="#top" className="flex items-center gap-3 group">
          <GsShield className="h-9 w-auto" />
          <div className="leading-tight">
            <div className="font-display text-base sm:text-lg tracking-wide text-warm-white uppercase">
              Golden State <span className="text-brass">Project Works</span>
            </div>
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.32em] text-warm-white/55">
              Built by Apache
            </div>
          </div>
        </a>
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-xs font-semibold uppercase tracking-[0.2em] text-warm-white/70 hover:text-brass transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a
            href="tel:+12139866277"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-warm-white/65 hover:text-brass transition-colors"
            aria-label="Call (213) 986-6277"
          >
            <Phone className="h-3.5 w-3.5" /> (213) 986-6277
          </a>
          {user ? (
            <Button
              variant="outlineLight"
              size="sm"
              onClick={handleSignOut}
              aria-label="Sign out"
              title={user.email ?? "Signed in"}
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </Button>
          ) : (
            <>
              <Button
                variant="outlineLight"
                size="sm"
                onClick={handleGoogle}
                aria-label="Sign in with Google"
              >
                <GoogleIcon />
                <span className="hidden sm:inline">Google</span>
              </Button>
              <Button
                variant="outlineLight"
                size="sm"
                onClick={handleApple}
                aria-label="Sign in with Apple"
              >
                <AppleIcon />
                <span className="hidden sm:inline">Apple</span>
              </Button>
            </>
          )}
          <Button variant="brass" size="sm" onClick={open}>
            Get My Estimate
          </Button>
        </div>
      </div>
    </header>
  );
};
